import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  useLocation,
  Navigate
} from "react-router-dom";
import React, { useState, useEffect, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";

import Navbar from "./Sections/Navbar";
import Footer from "./Sections/Footer";
// Below-the-fold homepage strips — lazy-loaded to keep them out of the
// home route's initial JS bundle (they don't render above the fold and
// they were inflating Total Blocking Time).
import { CMS_PAGES } from "./data/cityServiceSlugMap";
import NavbarPanel from "./components/SectionsPanel/NavbarPanel";
import FooterPanel from "./components/SectionsPanel/FooterPanel";
import useAuthWatcher from "./hooks/useAuthWatcher";
import usePageView from "./hooks/usePageView";
import useCityTitle from "./hooks/useCityTitle";
import CookieConsent from "./components/CookieConsent";
import HomePage from "./components/HomePage";
import NotFound from "./components/NotFound";

// ── Lazy-loaded: each route becomes a separate JS chunk ──────────────────────
const Contact              = React.lazy(() => import("./Sections/Contact"));
const Foundation           = React.lazy(() => import("./Sections/Foundation"));
// /blog/:slug — renders posts from the build-time snapshot (its own chunk, so
// the article bodies stay out of the main bundle)
const BlogRoute            = React.lazy(() => import("./components/BlogPostRoute"));
// /services/:slug — the service data and published service pages live in this chunk
const ServiceRoute         = React.lazy(() => import("./components/ServiceRoute"));
const Careers             = React.lazy(() => import("./Sections/Careers/Careers.jsx"));
const CareersInfoPage      = React.lazy(() => import("./components/CareersInfoPage"));
const Login                = React.lazy(() => import("./components/Login/Login"));
const Panel                = React.lazy(() => import("./components/Panel/Panel"));
const Application          = React.lazy(() => import("./components/Panel/Application"));
const Employees            = React.lazy(() => import("./components/Panel/Employees"));
const Settings             = React.lazy(() => import("./components/Panel/Settings"));
const PrivacyPolicy        = React.lazy(() => import("./components/PrivacyPolicy"));
const PrivacyPolicyFR      = React.lazy(() => import("./components/PrivacyPolicy.fr"));
const TermsOfService       = React.lazy(() => import("./components/TermsOfService"));
const TermsOfServiceFR     = React.lazy(() => import("./components/TermsOfService.fr"));
const AdminLogin           = React.lazy(() => import("./components/Admin/AdminLogin"));
const AdminDashboard       = React.lazy(() => import("./components/Admin/AdminDashboard"));
const AboutPage            = React.lazy(() => import("./components/AboutPage"));
const CompliancePage       = React.lazy(() => import("./components/CompliancePage"));
const IndustryPage         = React.lazy(() => import("./components/IndustryPage"));
const CaseStudyPage        = React.lazy(() => import("./components/CaseStudyPage"));
const CaseStudiesIndex     = React.lazy(() => import("./components/CaseStudiesIndex"));
const BlogListPage         = React.lazy(() => import("./components/BlogListPage"));
const TorontoPage          = React.lazy(() => import("./components/CityPages/TorontoPage"));
const MontrealPage         = React.lazy(() => import("./components/CityPages/MontrealPage"));
const OttawaPage           = React.lazy(() => import("./components/CityPages/OttawaPage"));
const QuebecCityPage       = React.lazy(() => import("./components/CityPages/QuebecCityPage"));
const TroisRivieresPage    = React.lazy(() => import("./components/CityPages/TroisRivieresPage"));
const NiagaraFallsPage     = React.lazy(() => import("./components/CityPages/NiagaraFallsPage"));
const LavalPage             = React.lazy(() => import("./components/CityPages/LavalPage"));
const CityServicePage      = React.lazy(() => import("./components/CityServicePage"));

// City + service pages on CityPageLayout (content from the city-pages CMS), by
// CMS page key. Their EN / FR addresses come from CMS_PAGES in
// data/cityServiceSlugMap.js — the table the sitemap and the FR link
// localizer read too — so a page's route, sitemap entry and links can't drift.
// index.js preloads the current page's chunk from these before hydrating.
export const CMS_CITY_SERVICE_LOADERS = {
  "montreal--event-security": () => import("./components/CityServicePages/MontrealEventSecurity"),
  "montreal--security-guards": () => import("./components/CityServicePages/MontrealSecurityGuards"),
  "montreal--construction-site-security": () => import("./components/CityServicePages/MontrealConstructionSecurity"),
  "montreal--mobile-patrol": () => import("./components/CityServicePages/MontrealMobilePatrol"),
  "toronto--event-security": () => import("./components/CityServicePages/TorontoEventSecurity"),
  "toronto--security-guards": () => import("./components/CityServicePages/TorontoSecurityGuards"),
  "toronto--construction-site-security": () => import("./components/CityServicePages/TorontoConstructionSecurity"),
  "toronto--mobile-patrol": () => import("./components/CityServicePages/TorontoMobilePatrol"),
  "ottawa--event-security": () => import("./components/CityServicePages/OttawaEventSecurity"),
  "ottawa--security-guards": () => import("./components/CityServicePages/OttawaSecurityGuards"),
  "ottawa--construction-site-security": () => import("./components/CityServicePages/OttawaConstructionSecurity"),
  "ottawa--mobile-patrol": () => import("./components/CityServicePages/OttawaMobilePatrol"),
  "laval--security-guards": () => import("./components/CityServicePages/LavalSecurityGuards"),
  "laval--event-security": () => import("./components/CityServicePages/LavalEventSecurity"),
  "quebec-city--security-guards": () => import("./components/CityServicePages/QuebecCitySecurityGuards"),
  "quebec-city--event-security": () => import("./components/CityServicePages/QuebecCityEventSecurity"),
  "quebec-city--construction-site-security": () => import("./components/CityServicePages/QuebecCityConstructionSecurity"),
  "quebec-city--mobile-patrol": () => import("./components/CityServicePages/QuebecCityMobilePatrol"),
  "niagara-falls--event-security": () => import("./components/CityServicePages/NiagaraFallsEventSecurity"),
  "niagara-falls--hospitality-security": () => import("./components/CityServicePages/NiagaraFallsHospitalitySecurity"),
};
const CMS_CITY_SERVICE_PAGES = Object.fromEntries(
  Object.entries(CMS_CITY_SERVICE_LOADERS).map(([key, load]) => [key, React.lazy(load)])
);

/** The <Route>s of the CMS city + service pages in one language (FR paths are relative to /fr). */
function cmsCityServiceRoutes(lang) {
  return Object.entries(CMS_PAGES)
    .filter(([key, p]) => p[lang] && CMS_CITY_SERVICE_PAGES[key])
    .map(([key, p]) => {
      const Page = CMS_CITY_SERVICE_PAGES[key];
      return <Route key={key} path={lang === "fr" ? p.fr.replace(/^\/fr\//, "") : p.en} element={<Page />} />;
    });
}

/* --- helpers --- */
function ScrollToTop() {
  const { pathname } = useLocation();
  
  React.useEffect(() => {
    // Instant scroll to top (no smooth behavior)
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    
    // Add loading class to prevent flash
    document.body.classList.add('page-loading');
    
    const timer = setTimeout(() => {
      document.body.classList.remove('page-loading');
    }, 300);
    
    return () => clearTimeout(timer);
  }, [pathname]);
  
  return null;
}


/* --- pages --- */

// Organization + AggregateRating JSON-LD for homepage

/* Layout keeps Navbar/Footer on every route */
function Layout() {
  return (
    <>
      <Navbar />
      <Outlet />
      <Footer />
    </>
  );
}

function PrivateRoute({ children }) {
  const isLoggedIn = Boolean(sessionStorage.getItem("token"));
  return isLoggedIn ? children : <Navigate to="/login" replace />;
}

function AuthWatcher() {
  useAuthWatcher();
  return null; // no UI
}

function PageViewTracker() {
  usePageView();
  return null; // no UI
}

function CityTitleSetter() {
  useCityTitle();
  return null;
}

function LayoutPanel() {
  return (
    <>
      <NavbarPanel />
      <Outlet />   {/* This will render your Panel with Boxes */}
      {/* <FooterPanel /> */}

      <FooterPanel /> 
    </>
  );
}

/* --- router --- */
export default function App() {
  const protectedRoutes = ["/panel", "/Application", "/Employees", "/Settings"];
  
  // Initialize language with deterministic default ('en') for SSR/prerender
  // safety; reconcile with localStorage after hydration.
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    // Skip during prerender so prerendered HTML matches client first render.
    if (typeof navigator !== 'undefined' && navigator.userAgent.includes('HeadlessChrome')) return;
    const savedLanguage = localStorage.getItem('preferredLanguage');
    if (savedLanguage && savedLanguage !== language) setLanguage(savedLanguage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save language preference to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('preferredLanguage', language);
  }, [language]);
  
  return (
    <HelmetProvider>
    <BrowserRouter>
      <ScrollToTop />
      <AuthWatcher protectedRoutes={protectedRoutes} />
      <PageViewTracker />
      <CityTitleSetter />

      <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
      <Routes>
        {/* ===== English (default) ===== */}
        <Route element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/toronto" element={<TorontoPage />} />
          <Route path="/montreal" element={<MontrealPage />} />
          <Route path="/ottawa" element={<OttawaPage />} />
          <Route path="/quebec-city" element={<QuebecCityPage />} />
          <Route path="/trois-rivieres" element={<TroisRivieresPage />} />
          <Route path="/niagara-falls" element={<NiagaraFallsPage />} />
          <Route path="/laval" element={<LavalPage />} />
          {/* CMS city + service pages (/montreal/event-security …), from CMS_PAGES */}
          {cmsCityServiceRoutes("en")}
          <Route path="/services/:slug" element={<ServiceRoute />} />
          <Route path="/industries/:slug" element={<IndustryPage />} />
          <Route path="/case-studies" element={<CaseStudiesIndex />} />
          <Route path="/case-studies/:slug" element={<CaseStudyPage />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/foundation" element={<Foundation />} />
          <Route path="/blog/:slug" element={<BlogRoute />} />


          <Route path="/login" element={<Login />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/security-careers" element={<CareersInfoPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/compliance" element={<CompliancePage />} />

          {/* City + Service combo pages (dynamic). Declared AFTER literal
              routes so /services/:slug, /blog/:slug, etc. always win. */}
          <Route path="/:citySlug/:serviceSlug" element={<CityServicePage />} />

          {/* 404 fallback (EN). The server answers unknown URLs with public/404.html
              (vercel.json); this covers client-side navigation. */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* ===== French mirror under /fr/* =====
            Same components; i18n provides FR strings via URL prefix */}
        <Route path="/fr" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="contact" element={<Contact />} />
          <Route path="services/:slug" element={<ServiceRoute />} />
          <Route path="industries/:slug" element={<IndustryPage />} />
          <Route path="etudes-de-cas" element={<CaseStudiesIndex />} />
          <Route path="etudes-de-cas/:slug" element={<CaseStudyPage />} />
          <Route path="blog" element={<BlogListPage />} />
          <Route path="blog/foundation" element={<Foundation />} />
          <Route path="blog/:slug" element={<BlogRoute />} />


          <Route path="toronto" element={<TorontoPage />} />
          <Route path="montreal" element={<MontrealPage />} />
          <Route path="ottawa" element={<OttawaPage />} />
          <Route path="quebec-city" element={<QuebecCityPage />} />
          <Route path="quebec" element={<QuebecCityPage />} />
          <Route path="trois-rivieres" element={<TroisRivieresPage />} />
          <Route path="niagara-falls" element={<NiagaraFallsPage />} />
          <Route path="laval" element={<LavalPage />} />
          {/* CMS city + service pages with a French version (/fr/montreal/agents-de-securite,
              /fr/quebec/agent-de-securite …), from CMS_PAGES */}
          {cmsCityServiceRoutes("fr")}
          <Route path="careers" element={<Careers />} />
          <Route path="security-careers" element={<CareersInfoPage />} />
          <Route path="privacy" element={<PrivacyPolicyFR />} />
          <Route path="terms" element={<TermsOfServiceFR />} />
          <Route path="conformite" element={<CompliancePage />} />

          {/* City + Service combo pages (FR, dynamic). Declared AFTER all
              literal /fr/* routes so they only catch genuine combos. */}
          <Route path=":citySlug/:serviceSlug" element={<CityServicePage />} />

          {/* 404 fallback (FR) */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* ===== Admin portal (standalone, no Layout) ===== */}
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />} />

        {/* ===== Panel / protected area (kept EN-only) ===== */}
        <Route element={<LayoutPanel />}>
          <Route
            path="/panel"
            element={
              <PrivateRoute>
                <Panel />
              </PrivateRoute>
            }
          />
          {/* Note: your paths use capital letters; keep consistent */}
          <Route
            path="/Application"
            element={
              <PrivateRoute>
                <Application />
              </PrivateRoute>
            }
          />
          <Route
            path="/Employees"
            element={
              <PrivateRoute>
                <Employees />
              </PrivateRoute>
            }
          />

           <Route
            path="/Settings"
            element={
              <PrivateRoute>
                <Settings/>
                
              </PrivateRoute>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      </Suspense>
      <CookieConsent />
    </BrowserRouter>
    </HelmetProvider>
  );
}
